import { useEffect, useRef, useState } from 'react';
import Chart from 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import Hammer from 'hammerjs';
import { getWeatherEmoji } from '../utils';

if (typeof window !== 'undefined') {
  window.Hammer = Hammer;
}

Chart.register(zoomPlugin);

const WeatherWidget = ({
  lat = 32.7767,
  lon = -96.797,
  locationName = 'Dallas, TX',
  height = 220,
  isFull = false,
  showDetails = false,
}) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [weatherData, setWeatherData] = useState(null);
  const [currentConditions, setCurrentConditions] = useState(null);
  const [activeInfo, setActiveInfo] = useState(null);
  const [showTopTemps, setShowTopTemps] = useState(true);
  const [alert, setAlert] = useState(null);
  const [currentHourIndex, setCurrentHourIndex] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&hourly=temperature_2m,apparent_temperature,precipitation,precipitation_probability,cloud_cover,wind_speed_10m&daily=sunrise,sunset,temperature_2m_max,temperature_2m_min,precipitation_probability_max,precipitation_sum,snowfall_sum,precipitation_hours,wind_speed_10m_max,uv_index_max,wind_direction_10m_dominant&timezone=auto&forecast_days=10&temperature_unit=fahrenheit&precipitation_unit=inch&wind_speed_unit=mph`;
    const aqiUrl = `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lon}&hourly=us_aqi&timezone=auto`;

    const cacheKey = `weather_${lat}_${lon}_${showDetails}`;
    const cachedStr = sessionStorage.getItem(cacheKey);
    if (cachedStr) {
      try {
        const cached = JSON.parse(cachedStr);
        if (Date.now() - cached.timestamp < 30 * 60 * 1000) { // 30 mins
          setWeatherData(cached.data);
          if (cached.data.current) {
            const cityTimezone = cached.data.timezone || 'UTC';
            const exactCurrentTime = new Date().toLocaleTimeString('en-US', {
              hour: 'numeric',
              minute: '2-digit',
              timeZone: cityTimezone,
            });
            setCurrentConditions({
              temp: Math.round(cached.data.current.temperature_2m),
              emoji: getWeatherEmoji(cached.data.current.weather_code),
              time: exactCurrentTime,
            });
            const currentHourStr = cached.data.current.time.substring(0, 13);
            let hourIdx = cached.data.hourly.time.findIndex((l) => l.startsWith(currentHourStr));
            setCurrentHourIndex(hourIdx === -1 ? 0 : hourIdx);
          }
          setLoading(false);
          return;
        }
      } catch (e) {
        console.warn('Weather cache error:', e);
      }
    }

    try {
      const response = await fetch(url);
      const data = await response.json();

      let finalData = { ...data };
      if (showDetails) {
        try {
          const aqiRes = await fetch(aqiUrl);
          const aqiData = await aqiRes.json();
          finalData.aqiData = aqiData;
        } catch (e) {
          console.error('Error fetching AQI:', e);
        }
      }

      sessionStorage.setItem(cacheKey, JSON.stringify({
        timestamp: Date.now(),
        data: finalData
      }));

      if (data.current) {
        const cityTimezone = data.timezone || 'UTC';
        const exactCurrentTime = new Date().toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          timeZone: cityTimezone,
        });
        setCurrentConditions({
          temp: Math.round(data.current.temperature_2m),
          emoji: getWeatherEmoji(data.current.weather_code),
          time: exactCurrentTime,
        });
      }

      const currentHourStr = data.current.time.substring(0, 13);
      let hourIdx = data.hourly.time.findIndex((l) => l.startsWith(currentHourStr));
      if (hourIdx === -1) hourIdx = 0;

      setCurrentHourIndex(hourIdx);
      
      setWeatherData(finalData);
      setLoading(false);

      // Fetch alerts
      fetch(`https://api.weather.gov/alerts/active?point=${lat},${lon}`, {
        headers: { 'User-Agent': '(MeteogramWidget, contact@example.com)' },
      })
        .then((res) => res.json())
        .then((alertData) => {
          if (alertData.features && alertData.features.length > 0) {
            setAlert(alertData.features[0].properties);
          }
        })
        .catch((e) => console.warn('NWS Alerts check skipped'));
    } catch (error) {
      console.error('Error fetching weather data:', error);
      setLoading(false);
    }
  };

  const renderInfoBox = (data, index, isHovering = false) => {
    if (!data || index < 0 || index >= data.hourly.time.length) return;

    const dateStr = data.hourly.time[index];
    const dateObj = new Date(dateStr + 'Z');

    const todayStr = data.current.time.split('T')[0];
    const thisDateStr = dateStr.split('T')[0];

    let timeFormatted = 'Now';
    if (isHovering) {
      let dayPrefix =
        todayStr === thisDateStr
          ? 'Today'
          : dateObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
      timeFormatted = `${dayPrefix} ${dateObj.toLocaleTimeString('en-US', { hour: 'numeric', timeZone: 'UTC' })}`;
    }

    const temp = Math.round(data.hourly.temperature_2m[index]);
    const feels = Math.round(data.hourly.apparent_temperature[index]);
    const precip = data.hourly.precipitation[index];
    const prob = data.hourly.precipitation_probability[index];
    const wind = Math.round(data.hourly.wind_speed_10m[index]);
    const cloud = data.hourly.cloud_cover[index];

    setActiveInfo({
      timeFormatted,
      temp,
      feels,
      precip,
      prob,
      wind,
      cloud,
    });
  };

  const renderChart = (data, hourIdx) => {
    if (!canvasRef.current) return;

    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const ctx = canvasRef.current.getContext('2d');

    const dayBackgroundPlugin = {
      id: 'dayBackground',
      beforeDraw: (chart) => {
        if (!chart.scales || !chart.scales.x || !chart.chartArea) return;
        const ctx = chart.ctx;
        ctx.save();
        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();

        const xAxis = chart.scales.x;
        const chartLabels = chart.data.labels;
        const topY = xAxis.top;
        const bottomY = xAxis.bottom;

        const dayColors = [
          '#1c0f0f',
          '#0f141c',
          '#150f1c',
          '#0f1c15',
          '#1c180f',
          '#0f1a1c',
          '#1c0f17',
        ];

        ctx.save();
        ctx.beginPath();
        ctx.rect(
          chart.chartArea.left,
          topY,
          chart.chartArea.right - chart.chartArea.left,
          chart.height - topY
        );
        ctx.clip();

        let lastX = xAxis.getPixelForValue(0);
        let dayIndex = 0;
        let currentDayNum = new Date(chartLabels[0].split('T')[0] + 'Z').getUTCDay();

        for (let i = 0; i < chartLabels.length; i++) {
          if (chartLabels[i].endsWith('T00:00') && i !== 0) {
            const x = xAxis.getPixelForValue(i);
            if (x > chart.chartArea.left && lastX < chart.chartArea.right) {
              ctx.fillStyle = `rgba(255, 255, 255, ${Math.max(0, 0.05 - dayIndex * 0.005)})`;
              ctx.fillRect(lastX, topY, x - lastX, bottomY - topY);
              ctx.fillStyle = dayColors[currentDayNum];
              ctx.fillRect(lastX, bottomY, x - lastX, chart.height - bottomY);
              ctx.beginPath();
              ctx.lineWidth = 2;
              ctx.strokeStyle = '#334155';
              ctx.moveTo(x, topY);
              ctx.lineTo(x, chart.height);
              ctx.stroke();
            }
            lastX = x;
            dayIndex++;
            currentDayNum = new Date(chartLabels[i].split('T')[0] + 'Z').getUTCDay();
          } else if (chartLabels[i].endsWith('T12:00')) {
            const x = xAxis.getPixelForValue(i);
            if (x >= chart.chartArea.left && x <= chart.chartArea.right) {
              ctx.beginPath();
              ctx.lineWidth = 2;
              ctx.strokeStyle = '#334155';
              ctx.moveTo(x, topY);
              ctx.lineTo(x, topY + 3);
              ctx.stroke();
            }
          }
        }

        const finalX = xAxis.getPixelForValue(chartLabels.length - 1);
        if (finalX > chart.chartArea.left && lastX < chart.chartArea.right) {
          ctx.fillStyle = `rgba(255, 255, 255, ${Math.max(0, 0.05 - dayIndex * 0.005)})`;
          ctx.fillRect(lastX, topY, finalX - lastX, bottomY - topY);
          ctx.fillStyle = dayColors[currentDayNum];
          ctx.fillRect(lastX, bottomY, finalX - lastX, chart.height - bottomY);
        }
        ctx.restore();
      },
    };

    const hoverLinePlugin = {
      id: 'hoverLine',
      afterDatasetsDraw: (chart) => {
        if (!chart.scales || !chart.scales.x || !chart.scales['y-temp'] || !chart.chartArea) return;
        const ctx = chart.ctx;
        const xAxis = chart.scales.x;
        const topY = chart.scales['y-temp'].top;
        const bottomY = chart.scales['y-temp'].bottom;
        let targetX;
        if (chart.tooltip && chart.tooltip._active && chart.tooltip._active.length) {
          targetX = chart.tooltip._active[0].element.x;
        } else {
          targetX = xAxis.getPixelForValue(hourIdx);
        }
        if (targetX >= chart.chartArea.left && targetX <= chart.chartArea.right) {
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(targetX, topY);
          ctx.lineTo(targetX, bottomY);
          ctx.lineWidth = 2;
          ctx.globalCompositeOperation = 'difference';
          ctx.strokeStyle = '#ffffff';
          ctx.stroke();
          ctx.restore();
        }
      },
    };

    const freezingLinePlugin = {
      id: 'freezingLine',
      beforeDraw: (chart) => {
        if (!chart.scales || !chart.scales['y-temp'] || !chart.chartArea) return;
        const ctx = chart.ctx;
        const yAxis = chart.scales['y-temp'];
        const yPixel = yAxis.getPixelForValue(32);
        if (yPixel >= chart.chartArea.top && yPixel <= chart.chartArea.bottom) {
          ctx.save();
          ctx.beginPath();
          ctx.lineWidth = 1.5;
          ctx.strokeStyle = 'rgba(239, 68, 68, 0.6)';
          ctx.moveTo(chart.chartArea.left, yPixel);
          ctx.lineTo(chart.chartArea.right, yPixel);
          ctx.stroke();
          ctx.restore();
        }
      },
    };

    const topBarInfoPlugin = {
      id: 'topBarInfo',
      afterDraw: (chart) => {
        if (!chart.scales || !chart.scales.x || !chart.chartArea) return;
        const ctx = chart.ctx;
        const xAxis = chart.scales.x;
        const chartLabels = chart.data.labels;

        ctx.save();
        ctx.beginPath();
        ctx.rect(
          chart.chartArea.left,
          0,
          chart.chartArea.right - chart.chartArea.left,
          chart.height
        );
        ctx.clip();

        ctx.font = 'bold 11px ui-sans-serif, system-ui, sans-serif';
        ctx.textBaseline = 'bottom';
        const yPos = chart.chartArea.top - 8;

        for (let i = 0; i < chartLabels.length; i++) {
          if (chartLabels[i].endsWith('T00:00') && i !== 0) {
            const x = xAxis.getPixelForValue(i);
            if (x >= chart.chartArea.left && x <= chart.chartArea.right) {
              ctx.beginPath();
              ctx.lineWidth = 2;
              ctx.strokeStyle = '#334155';
              ctx.moveTo(x, chart.chartArea.top - 24);
              ctx.lineTo(x, chart.chartArea.top);
              ctx.stroke();
            }
          }

          if (chartLabels[i].endsWith('T12:00')) {
            const x = xAxis.getPixelForValue(i);
            if (x > chart.chartArea.left - 100 && x < chart.chartArea.right + 100) {
              const dateStr = chartLabels[i].split('T')[0];
              const dayIdx = data.daily.time.indexOf(dateStr);
              if (dayIdx !== -1) {
                const textOffset = 8;
                if (showTopTemps) {
                  const min = Math.round(data.daily.temperature_2m_min[dayIdx]);
                  const max = Math.round(data.daily.temperature_2m_max[dayIdx]);
                  ctx.textAlign = 'right';
                  ctx.fillStyle = '#60a5fa';
                  ctx.fillText(`${min}°`, x - textOffset, yPos);
                  ctx.textAlign = 'left';
                  ctx.fillStyle = '#fb923c';
                  ctx.fillText(`${max}°`, x + textOffset, yPos);
                } else {
                  const rise = new Date(data.daily.sunrise[dayIdx])
                    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                    .replace(' AM', 'am')
                    .replace(' PM', 'pm');
                  const set = new Date(data.daily.sunset[dayIdx])
                    .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
                    .replace(' AM', 'am')
                    .replace(' PM', 'pm');
                  ctx.textAlign = 'right';
                  ctx.fillStyle = '#60a5fa';
                  ctx.fillText(rise, x - textOffset, yPos);
                  ctx.textAlign = 'left';
                  ctx.fillStyle = '#fb923c';
                  ctx.fillText(set, x + textOffset, yPos);
                }
              }
            }
          }
        }
        ctx.restore();
      },
    };

    chartRef.current = new Chart(ctx, {
      type: 'line',
      plugins: [dayBackgroundPlugin, hoverLinePlugin, topBarInfoPlugin, freezingLinePlugin],
      data: {
        labels: data.hourly.time,
        datasets: [
          {
            label: 'Cloud Cover (%)',
            data: data.hourly.cloud_cover,
            borderColor: '#cbd5e1',
            backgroundColor: 'rgba(203, 213, 225, 0.3)',
            borderWidth: 1,
            tension: 0.4,
            fill: true,
            yAxisID: 'y-cloud',
            pointRadius: 0,
          },
          {
            label: 'Precipitation Probability (%)',
            data: data.hourly.precipitation_probability,
            borderColor: '#60a5fa',
            backgroundColor: 'rgba(96, 165, 250, 0.2)',
            borderWidth: 1,
            tension: 0.4,
            fill: true,
            yAxisID: 'y-cloud',
            pointRadius: 0,
          },
          {
            label: 'Precipitation (in)',
            data: data.hourly.precipitation,
            type: 'bar',
            backgroundColor: '#3b82f6',
            borderRadius: 4,
            yAxisID: 'y-precip',
          },
          {
            label: 'Wind Speed (mph)',
            data: data.hourly.wind_speed_10m,
            borderColor: '#22c55e',
            borderDash: [5, 5],
            borderWidth: 2,
            tension: 0.4,
            fill: false,
            yAxisID: 'y-wind',
            pointRadius: 0,
          },
          {
            label: 'Temperature (°F)',
            data: data.hourly.temperature_2m,
            borderColor: '#ffffff',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 3,
            tension: 0.4,
            fill: false,
            yAxisID: 'y-temp',
            pointRadius: 0,
          },
          {
            label: 'Feels Like (°F)',
            data: data.hourly.apparent_temperature,
            borderColor: 'transparent',
            borderWidth: 0,
            tension: 0.4,
            fill: {
              target: '-1',
              above: 'rgba(239, 68, 68, 0.5)',
              below: 'rgba(59, 130, 246, 0.5)',
            },
            yAxisID: 'y-temp',
            pointRadius: 0,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { left: 0, right: 0, top: 26, bottom: 0 },
        },
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: function (context) {
              const tooltipModel = context.tooltip;
              if (tooltipModel.opacity === 0) {
                renderInfoBox(data, hourIdx, false);
                return;
              }
              if (tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0) {
                renderInfoBox(data, tooltipModel.dataPoints[0].dataIndex, true);
              }
            },
          },
          zoom: {
            pan: {
              enabled: true,
              mode: 'x',
            },
            zoom: {
              wheel: { enabled: false },
              pinch: { enabled: false },
            },
          },
        },
        scales: {
          x: {
            min: data.hourly.time[0],
            max: isFull ? data.hourly.time[data.hourly.time.length - 1] : data.hourly.time[95],
            grid: {
              display: true,
              drawOnChartArea: true,
              drawTicks: false,
              color: (context) => {
                if (context.tick && data.hourly.time[context.tick.value]?.endsWith('T00:00')) {
                  return '#334155';
                }
                return 'transparent';
              },
              lineWidth: (context) => {
                if (context.tick && data.hourly.time[context.tick.value]?.endsWith('T00:00')) {
                  return 2;
                }
                return 1;
              },
            },
            ticks: {
              maxRotation: 0,
              autoSkip: false,
              padding: 1,
              callback: function (val, index) {
                const dateStr = this.getLabelForValue(val);
                if (!dateStr) return '';
                if (dateStr.endsWith('T12:00')) {
                  const dateObj = new Date(dateStr + 'Z');
                  const todayStr = data.current.time.split('T')[0];
                  const thisDateStr = dateStr.split('T')[0];
                  const dayName =
                    todayStr === thisDateStr
                      ? 'Today'
                      : dateObj.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' });
                  return dayName;
                }
                return '';
              },
              font: { size: 9, weight: 'bold', lineHeight: 1.2 },
              color: '#94a3b8',
            },
          },
          'y-temp': {
            type: 'linear',
            position: 'left',
            suggestedMin: 20,
            suggestedMax: 100,
            grid: { color: '#1e293b', drawTicks: false },
            ticks: { font: { size: 9 }, padding: 2, color: '#94a3b8' },
          },
          'y-precip': {
            type: 'linear',
            position: 'right',
            suggestedMin: 0,
            suggestedMax: 0.5,
            grid: { display: false },
            ticks: { display: false },
          },
          'y-wind': {
            type: 'linear',
            position: 'right',
            suggestedMin: 0,
            suggestedMax: 40,
            grid: { display: false },
            ticks: { display: false },
          },
          'y-cloud': {
            type: 'linear',
            position: 'right',
            suggestedMin: 0,
            suggestedMax: 100,
            grid: { display: false },
            ticks: { display: false },
          },
        },
      },
    });

    const canvas = canvasRef.current;
    const wheelHandler = (e) => {
      if (chartRef.current) {
        e.preventDefault();
        const delta = e.deltaX || e.deltaY;
        chartRef.current.pan({ x: -delta });
      }
    };
    canvas.addEventListener('wheel', wheelHandler, { passive: false });
    return () => canvas.removeEventListener('wheel', wheelHandler);
  };

  useEffect(() => {
    if (!canvasRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (chartRef.current) {
        // Use requestAnimationFrame to avoid "ResizeObserver loop limit exceeded"
        requestAnimationFrame(() => {
          chartRef.current?.resize();
        });
      }
    });

    const container = canvasRef.current.parentElement;
    if (container) {
      resizeObserver.observe(container);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    fetchData();
    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [lat, lon]);

  useEffect(() => {
    if (!loading && weatherData) {
      renderChart(weatherData, currentHourIndex);
      renderInfoBox(weatherData, currentHourIndex, false);
    }
  }, [loading, weatherData, currentHourIndex]);

  useEffect(() => {
    if (chartRef.current) {
      chartRef.current.update();
    }
  }, [showTopTemps]);

  return (
    <div className="w-full bg-white overflow-hidden border-b border-zinc-200">
      {/* Widget Header */}
      <div className="bg-slate-800 text-white px-3 py-1.5 flex justify-between items-center gap-2 min-w-0">
        <div className="flex items-center gap-2 overflow-hidden min-w-0">
          {currentConditions && (
            <div className="text-sm font-bold flex items-center gap-1 shrink-0">
              {currentConditions.temp}°F{' '}
              <span className="text-base">{currentConditions.emoji}</span>
            </div>
          )}
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tight truncate min-w-0">
            {locationName}
          </p>
        </div>

        {activeInfo && (
          <div className="flex items-center gap-2 shrink-0 ml-auto">
            <div className="text-[10px] font-bold text-white leading-none whitespace-nowrap">
              {activeInfo.timeFormatted}
            </div>
            <div className="flex items-center gap-1.5 text-[9px] font-semibold">
              <span className="text-white flex items-center gap-0.5">
                <span className="text-[8px] text-slate-400 font-normal">T</span>
                {activeInfo.temp}°
              </span>
              <span className="text-blue-400 flex items-center gap-0.5">
                <span className="text-[8px] text-slate-400 font-normal">R</span>
                {activeInfo.precip}&quot;
              </span>
              <span className="text-green-400 flex items-center gap-0.5">
                <span className="text-[8px] text-slate-400 font-normal">W</span>
                {activeInfo.wind}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Widget Body */}
      <div className="relative bg-black" style={{ height: `${height}px` }}>
        {alert && (
          <div className="absolute top-0 left-0 right-0 z-20 bg-rose-500 text-white p-1.5 font-bold flex items-center gap-2 text-[9px] leading-tight">
            <span className="shrink-0">⚠️</span>
            <span className="truncate">
              {alert.event}: {alert.headline}
            </span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-full">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
          </div>
        ) : (
          <>
            <button
              onClick={() => setShowTopTemps(!showTopTemps)}
              className="absolute top-1 left-1 z-10 text-[10px] cursor-pointer bg-transparent border-none p-0 drop-shadow-sm hover:scale-110 transition-transform"
            >
              {showTopTemps ? '🔵' : '🟡'}
            </button>
            <canvas
              ref={canvasRef}
              id="meteogram"
              style={{ cursor: 'grab', touchAction: 'pan-y', width: '100%', height: '100%' }}
              onMouseDown={(e) => (e.currentTarget.style.cursor = 'grabbing')}
              onMouseUp={(e) => (e.currentTarget.style.cursor = 'grab')}
            ></canvas>
          </>
        )}
      </div>
      
      {showDetails && weatherData && weatherData.daily && (
        (() => {
          const todayStr = weatherData.current?.time?.split('T')[0];
          let dayIdx = weatherData.daily.time.findIndex(t => t === todayStr);
          if (dayIdx === -1) dayIdx = 0;
          
          const precip = weatherData.daily.precipitation_probability_max?.[dayIdx];
          const precipSum = weatherData.daily.precipitation_sum?.[dayIdx];
          const snowSum = weatherData.daily.snowfall_sum?.[dayIdx];
          const precipHours = weatherData.daily.precipitation_hours?.[dayIdx];
          const wind = weatherData.daily.wind_speed_10m_max?.[dayIdx];
          const windDir = weatherData.daily.wind_direction_10m_dominant?.[dayIdx];
          const uv = weatherData.daily.uv_index_max?.[dayIdx];
          
          let aqiInfo = null;
          if (weatherData.aqiData && weatherData.aqiData.hourly && weatherData.aqiData.hourly.us_aqi) {
            const currentHourStr = weatherData.current.time.substring(0, 13);
            const aqiHourIdx = weatherData.aqiData.hourly.time.findIndex(t => t.startsWith(currentHourStr));
            if (aqiHourIdx !== -1) {
              const currentAqi = weatherData.aqiData.hourly.us_aqi[aqiHourIdx];
              if (currentAqi !== null && currentAqi !== undefined) {
                 if (currentAqi <= 50) aqiInfo = { icon: '🟢', label: `${currentAqi} Good`, color: 'text-green-500' };
                 else if (currentAqi <= 100) aqiInfo = { icon: '🟡', label: `${currentAqi} Moderate`, color: 'text-yellow-500' };
                 else if (currentAqi <= 150) aqiInfo = { icon: '🟠', label: `${currentAqi} Sensitive`, color: 'text-orange-500' };
                 else if (currentAqi <= 200) aqiInfo = { icon: '🔴', label: `${currentAqi} Unhealthy`, color: 'text-red-500' };
                 else if (currentAqi <= 300) aqiInfo = { icon: '🟣', label: `${currentAqi} Very Unhealthy`, color: 'text-purple-500' };
                 else aqiInfo = { icon: '🟤', label: `${currentAqi} Hazardous`, color: 'text-rose-900' };
              }
            }
          }
          
          const getWindArrow = (deg) => {
            if (deg === undefined || deg === null) return '';
            const dirs = ['⬇️','↙️','⬅️','↖️','⬆️','↗️','➡️','↘️'];
            return dirs[Math.round(deg / 45) % 8];
          };

          return (
            <div className="grid grid-cols-2 gap-2 text-xs font-bold text-zinc-600 bg-white p-3 border-b border-zinc-100">
              {(precipSum > 0 || snowSum > 0) ? (
                <div className="flex items-center gap-1.5 text-blue-600">
                  <span>{snowSum > 0 ? '❄️' : '🌧️'}</span>
                  <span>{precip}% ({(precipSum || snowSum).toFixed(2)}in / {precipHours}h)</span>
                </div>
              ) : (precip !== undefined) ? (
                <div className="flex items-center gap-1.5">
                  <span>🌧️</span>
                  <span>{precip}%</span>
                </div>
              ) : null}
              
              {wind !== undefined && (
                <div className="flex items-center gap-1.5">
                  <span>💨</span>
                  <span>{Math.round(wind)}mph {getWindArrow(windDir)}</span>
                </div>
              )}

              {uv !== null && uv !== undefined && (
                <div className="flex items-center gap-1.5">
                  <span>☀️</span>
                  <span>UV: {Math.round(uv)}</span>
                </div>
              )}

              {aqiInfo && (
                <div className={`flex items-center gap-1.5 ${aqiInfo.color}`}>
                  <span>{aqiInfo.icon}</span>
                  <span>AQI: {aqiInfo.label}</span>
                </div>
              )}
            </div>
          );
        })()
      )}
    </div>
  );
};

export default WeatherWidget;
